/**
 * @file 出牌估值 —— `design/gdd/06-ai-strategy.md` §4 / §6.2 / §6.3。
 *
 * 对手牌中每一张候选牌 `t` 计算「打出它的代价」，选代价最小者打出：
 *
 * ```
 * cost(t) = W_EFF * effCost(t) + W_SAFE * dangerCost(t)
 *         + W_YAKU * yakuCost(t) + W_FLEX * flexCost(t)
 * ```
 *
 * `dangerCost` 是四川麻将的独门优势：对手的缺门是公开信息，打他缺门的牌 100% 安全。
 * 困难档的 `W_SAFE` 由 `dynamicSafeWeight`（§6.2）动态计算，实现「该进攻时进攻，
 * 该防守时防守」。随机扰动 ε（§6.3）在 `pickWithNoise` 中实现。
 *
 * 纯函数、平台无关。
 */

import type { RngState, Suit } from '../types';
import { keyOfKind, suitToIndex } from '../tiles';
import { nextRandom, rngInt } from '../prng';
import type { DifficultyPreset } from './params';
import { copyCounts, shantenForMode } from './shanten';
import { ukeire } from './ukeire';
import type { AIMeld, AIObservation, AIOpponentInfo, TileCounts, TileKey } from './types';

/**
 * 估值上下文：一次出牌估值所需的全部派生状态。
 * 由 `decide()` 组装一次，供所有候选牌复用（避免反复拷贝手牌）。
 */
export interface ValuationContext {
  readonly obs: AIObservation;
  /** 手牌计数（`obs.hand` 的副本，估值函数内部再按需拷贝）。 */
  readonly counts: TileCounts;
  readonly meldCount: number;
  readonly queSuit: Suit | null;
  /** 「已见张数」表。 */
  readonly seen: Uint8Array;
  readonly cfg: DifficultyPreset;
}

/** 构造估值上下文。 */
export function makeValuationContext(
  obs: AIObservation,
  seen: Uint8Array,
  cfg: DifficultyPreset,
): ValuationContext {
  return {
    obs,
    counts: copyCounts(obs.hand),
    meldCount: obs.melds.length,
    queSuit: obs.queSuit,
    seen,
    cfg,
  };
}

/* ─────────────────────────── §4.3.1 牌面固有风险 ─────────────────────────── */

/**
 * 牌面固有风险系数：中张（4/5/6）成顺路径最多，最危险。
 * 1/9 只能作边张或刻子，成胡路径少。
 */
export function tileRiskProfile(tileKey: TileKey): number {
  const rank = (tileKey % 9) + 1;
  if (rank === 1 || rank === 9) return 0.45;
  if (rank === 2 || rank === 8) return 0.7;
  if (rank === 3 || rank === 7) return 0.9;
  return 1.0;
}

/* ─────────────────────────── §4.3.2 对手威胁度 ─────────────────────────── */

/** 取副露花色序号；暗杠（kind 未知）返回 -1。 */
function suitIndexOfMeld(m: AIMeld): number {
  return m.kind === null ? -1 : Math.floor(keyOfKind(m.kind) / 9);
}

/**
 * 对手威胁度（§4.3.2）。五个信号叠加后封顶 3.0。
 * - 信号 1：副露数（碰杠越多越接近听牌）；
 * - 信号 2：打牌轮数（巡目越深听牌概率越高）；
 * - 信号 3：最近 4 张里中张（4/5/6）数量（开始打中张 = 手里好牌都用上了）；
 * - 信号 4：单一花色副露 ≥ 2 → 清一色/对对胡倾向，威胁 ×1.7
 *   （★ 暗杠牌面未知不参与花色判定，保守处理）；
 * - 信号 5：已打完缺门很久还在打 → 在做大牌。
 */
export function threatLevel(o: AIOpponentInfo, obs: AIObservation): number {
  if (o.status === 'WON') return 0;

  let threat = 0.25; // 基础威胁
  threat += o.melds.length * 0.3; // 信号 1
  threat += Math.min(obs.turnIndex, 18) * 0.035; // 信号 2

  const recent = o.discards.slice(-4); // 信号 3
  let midCount = 0;
  for (const t of recent) {
    const rank = (t % 9) + 1;
    if (rank >= 4 && rank <= 6) midCount++;
  }
  threat += midCount * 0.18;

  // 信号 4：明副露 ≥ 2 且同一花色
  const suits = new Set<number>();
  let knownMeldCount = 0;
  for (const m of o.melds) {
    const s = suitIndexOfMeld(m);
    if (s >= 0) {
      suits.add(s);
      knownMeldCount++;
    }
  }
  if (knownMeldCount >= 2 && suits.size === 1) threat *= 1.7;

  // 信号 5：已打完缺门很久却还在打，说明在做大牌
  if (o.hasClearedQue && o.discards.length >= 10) threat += 0.2;

  return Math.min(threat, 3.0);
}

/* ─────────────────────────── §4.3 安全度项 ─────────────────────────── */

/**
 * 安全度项（§4.3）。四川麻将的独门优势：
 * - 打对手缺门的牌 100% 安全（danger = 0，直接跳过）；
 * - 现物（他打过这张牌）贡献 0.05（已打出说明不需要）；
 * - 否则 = 牌面固有风险 × 剩余张数因子 × 对手威胁度。
 */
export function dangerCost(tileKey: TileKey, ctx: ValuationContext): number {
  if (ctx.cfg.difficulty === 'EASY') return 0; // 简单 AI 完全不防守

  let total = 0;
  const tileSuit = Math.floor(tileKey / 9);
  for (const o of ctx.obs.opponents) {
    if (o.status === 'WON') continue;

    // ★★★ 川麻独有：对手缺门是公开的，打他缺门的牌 100% 安全 ★★★
    if (o.queSuit !== null && tileSuit === suitToIndex(o.queSuit)) continue;

    let contribution: number;
    if (o.discards.includes(tileKey)) {
      contribution = 0.05; // 现物
    } else {
      const base = tileRiskProfile(tileKey);
      const live = Math.max(0, 4 - (ctx.seen[tileKey] ?? 0));
      // 剩余张数越少，别人手上有对子/搭子的概率越低
      const liveFactor = [0.15, 0.55, 0.85, 1.0, 1.0][live] ?? 1.0;
      contribution = base * liveFactor;
    }
    total += threatLevel(o, ctx.obs) * contribution;
  }
  return total;
}

/* ─────────────────────────── §4.2 牌效率项 ─────────────────────────── */

/**
 * 2 步前瞻（§6.1 前瞻深度列 / A-4）：
 * 打掉 `tileKey` 后，模拟每一种可能摸到的牌，计算向听数改善的期望值。
 * 作为 `effCost` 的附加奖励项（量级 < 1000，绝不压倒向听数主导项）。
 *
 * ⚠ 该启发式是 §6.1「2 步前瞻」的落地实现；`06` 未给出伪代码，公式为本实现的
 * 工程化选择。若 M2 实测超时（A-4），降级方案为 `useTwoStep: false` 走 1 步。
 */
export function twoStepAdvantage(tileKey: TileKey, ctx: ValuationContext): number {
  const c = copyCounts(ctx.counts);
  c[tileKey] = (c[tileKey] ?? 0) - 1;
  const sA = shantenForMode(c, ctx.meldCount, ctx.queSuit, ctx.cfg.shantenMode);

  let totalLive = 0;
  let adv = 0;
  for (let k = 0; k < 27; k++) {
    if (ctx.queSuit !== null && Math.floor(k / 9) === suitToIndex(ctx.queSuit)) continue;
    if ((c[k] ?? 0) >= 4) continue;
    const live = 4 - (ctx.seen[k] ?? 0);
    if (live <= 0) continue;
    c[k] = (c[k] ?? 0) + 1;
    const s2 = shantenForMode(c, ctx.meldCount, ctx.queSuit, ctx.cfg.shantenMode);
    if (s2 < sA) adv += (sA - s2) * live;
    c[k] = (c[k] ?? 0) - 1;
    totalLive += live;
  }
  if (totalLive === 0) return 0;
  return adv / totalLive; // 每次摸牌的期望向听改善
}

/**
 * 牌效率项（§4.2）。
 *
 * `s * 1000` 的量级保证**向听数永远压倒进张数** —— 绝不会为了多 2 张进张而多退 1 向听。
 * `min(uk, 40)` 防止极端手牌下进张项喧宾夺主。
 *
 * 进张口径按难度（§6.1）：`NONE`（不算）/ `KINDS`（种数）/ `TOTAL`（总枚数）。
 */
export function effCost(tileKey: TileKey, ctx: ValuationContext): number {
  const c = copyCounts(ctx.counts);
  c[tileKey] = (c[tileKey] ?? 0) - 1;
  const s = shantenForMode(c, ctx.meldCount, ctx.queSuit, ctx.cfg.shantenMode);

  let uk = 0;
  if (ctx.cfg.ukeireMode !== 'NONE') {
    const u = ukeire(c, ctx.meldCount, ctx.queSuit, ctx.seen);
    uk = ctx.cfg.ukeireMode === 'TOTAL' ? u.total : u.tiles.length;
  }

  let result = s * 1000 - Math.min(uk, 40) * 8;
  if (ctx.cfg.useTwoStep && ctx.cfg.ukeireMode !== 'NONE') {
    result -= twoStepAdvantage(tileKey, ctx) * 60;
  }
  return result;
}

/* ─────────────────────────── §4.4 番型价值项 ─────────────────────────── */

/**
 * 番型价值项（§4.4）。四类倾向：
 * - 清一色：某门占比 ≥ 9 张时，打出该门牌代价大；
 * - 七对：无副露且对子 ≥ 4 时，拆对子代价大；
 * - 根：4 张相同别拆；
 * - 对对胡：拆刻子代价大。
 */
export function yakuCost(tileKey: TileKey, ctx: ValuationContext): number {
  if (ctx.cfg.difficulty === 'EASY') return 0;

  let cost = 0;

  // 清一色倾向：统计手牌 + 副露中每门总张数
  const suitCount = [0, 0, 0];
  for (let k = 0; k < 27; k++) {
    suitCount[Math.floor(k / 9)] = (suitCount[Math.floor(k / 9)] ?? 0) + (ctx.counts[k] ?? 0);
  }
  for (const m of ctx.obs.melds) {
    if (m.kind === null) continue;
    const s = Math.floor(keyOfKind(m.kind) / 9);
    suitCount[s] = (suitCount[s] ?? 0) + (m.type === 'peng' ? 3 : 4);
  }
  let dominant = -1;
  let domCount = 0;
  for (let s = 0; s < 3; s++) {
    if (ctx.queSuit !== null && s === suitToIndex(ctx.queSuit)) continue;
    const sc = suitCount[s] ?? 0;
    if (sc > domCount) {
      domCount = sc;
      dominant = s;
    }
  }
  if (dominant >= 0 && domCount >= 9 && Math.floor(tileKey / 9) === dominant) {
    cost += (domCount - 8) * 1.5;
  }

  // 七对倾向
  let pairs = 0;
  for (let k = 0; k < 27; k++) pairs += Math.floor((ctx.counts[k] ?? 0) / 2);
  if (ctx.meldCount === 0 && pairs >= 4 && (ctx.counts[tileKey] ?? 0) === 2) {
    cost += (pairs - 3) * 2.0;
  }

  // 根的价值
  if ((ctx.counts[tileKey] ?? 0) === 4) cost += 3.0;

  // 对对胡倾向
  if ((ctx.counts[tileKey] ?? 0) === 3) cost += 1.2;

  return cost;
}

/* ─────────────────────────── §4.5 灵活性项 ─────────────────────────── */

/**
 * 灵活性项（§4.5）：打掉后，`t` 的邻牌是否变成孤张。
 * 邻位（±1）损失 0.6，隔位（±2）损失 0.25。
 */
export function flexCost(tileKey: TileKey, ctx: ValuationContext): number {
  let penalty = 0;
  const suit = Math.floor(tileKey / 9);
  const rank = tileKey % 9;
  for (const d of [-2, -1, 1, 2]) {
    const rr = rank + d;
    if (rr < 0 || rr > 8) continue;
    const k = suit * 9 + rr;
    if ((ctx.counts[k] ?? 0) > 0) penalty += Math.abs(d) === 1 ? 0.6 : 0.25;
  }
  return penalty;
}

/* ─────────────────────────── §6.2 困难档动态防守权重 ─────────────────────────── */

/**
 * 困难档动态 `W_SAFE`（§6.2）。
 *
 * 曲线产生的可观察行为：
 * - 自己听牌（shanten 0）→ 0.40，冲，几乎不防；
 * - 向听 1 → 0.40 + maxThreat × 0.30；
 * - 向听 2 → 0.70 + maxThreat × 0.45；
 * - 向听远 → min(1.80, 1.00 + maxThreat × 0.60)，转防守，弃胡保分。
 */
export function dynamicSafeWeight(ctx: ValuationContext): number {
  const myShanten = shantenForMode(ctx.counts, ctx.meldCount, ctx.queSuit, ctx.cfg.shantenMode);
  let maxThreat = 0;
  for (const o of ctx.obs.opponents) {
    if (o.status === 'WON') continue;
    maxThreat = Math.max(maxThreat, threatLevel(o, ctx.obs));
  }
  if (myShanten === 0) return 0.4;
  if (myShanten === 1) return 0.4 + maxThreat * 0.3;
  if (myShanten === 2) return 0.7 + maxThreat * 0.45;
  return Math.min(1.8, 1.0 + maxThreat * 0.6);
}

/* ─────────────────────────── §4.1 总评分 ─────────────────────────── */

/**
 * 打出某张牌的总代价（§4.1）。
 * `W_SAFE`：困难档走 `dynamicSafeWeight`，其余档用预设基线。
 */
export function discardCost(tileKey: TileKey, ctx: ValuationContext): number {
  const wSafe =
    ctx.cfg.difficulty === 'HARD' ? dynamicSafeWeight(ctx) : ctx.cfg.wSafety;
  return (
    ctx.cfg.wEfficiency * effCost(tileKey, ctx) +
    wSafe * dangerCost(tileKey, ctx) +
    ctx.cfg.wYaku * yakuCost(tileKey, ctx) +
    ctx.cfg.wFlex * flexCost(tileKey, ctx)
  );
}

/* ─────────────────────────── 合法出牌集合（QC-1） ─────────────────────────── */

/**
 * 合法出牌集合（`02` §1.5 QC-1）：
 * **若手牌仍有缺门牌，候选集合仅含缺门牌**（强制清缺）；否则为手牌中全部牌种。
 *
 * @param counts  手牌计数
 * @param queSuit 自己定缺；`null` 表示未定缺（此时不强制清缺）
 */
export function getLegalDiscards(counts: TileCounts, queSuit: Suit | null): TileKey[] {
  const out: TileKey[] = [];
  if (queSuit !== null) {
    const base = suitToIndex(queSuit) * 9;
    let hasQue = false;
    for (let r = 0; r < 9; r++) {
      if ((counts[base + r] ?? 0) > 0) {
        hasQue = true;
        break;
      }
    }
    if (hasQue) {
      for (let r = 0; r < 9; r++) if ((counts[base + r] ?? 0) > 0) out.push(base + r);
      return out;
    }
  }
  for (let k = 0; k < 27; k++) if ((counts[k] ?? 0) > 0) out.push(k);
  return out;
}

/* ─────────────────────────── §6.3 随机扰动 ε ─────────────────────────── */

/**
 * 带噪声的出牌选择（§6.3）。
 *
 * - `rng.next() < ε` 时从前 3 名中随机（而非全体随机 —— 全随机会显得像 bug
 *   而非像新手；前 3 名随机产生「合理但次优」的选择，恰好就是新手表现）；
 * - 否则取成本最小者。
 *
 * @returns 选中的候选（含 cost）与推进后的 `rng`
 */
export function pickWithNoise(
  scored: readonly { readonly tile: TileKey; readonly cost: number }[],
  epsilon: number,
  rng: RngState,
): { readonly tile: TileKey; readonly cost: number; readonly rng: RngState } {
  if (scored.length === 0) {
    throw new Error('[@majiang/core] pickWithNoise 收到空候选集');
  }
  const sorted = scored.slice().sort((a, b) => a.cost - b.cost);
  const r1 = nextRandom(rng);
  let s = r1.state;
  let chosen: { readonly tile: TileKey; readonly cost: number };
  if (r1.value < epsilon && sorted.length > 1) {
    const topN = Math.min(3, sorted.length);
    const ri = rngInt(s, topN);
    s = ri.state;
    chosen = sorted[ri.value]!;
  } else {
    chosen = sorted[0]!;
  }
  return { tile: chosen.tile, cost: chosen.cost, rng: s };
}
